import React, { useContext, useState, useEffect } from "react";
import axios from "axios";
import AdvisorNavbar from "../layout/AdvisorNavbar";
import LocContext from "../context/locContext";
import BseLoginCard from "../common/BseLoginCard";
import { Link, useNavigate } from "react-router-dom";
import { FiUsers, FiAlertCircle, FiXCircle, FiCheckCircle, FiDollarSign, FiTrendingUp, FiClock, FiFileText, FiPlus, FiEye } from "react-icons/fi";
import Swal from "sweetalert2";

export default function AgentDashboard() {
  const { userData } = useContext(LocContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalClients: 0,
    kycPending: 0,
    failedOrders: 5,
    activeTasks: 12,
    totalRevenue: 245000,
    monthlyRevenue: 45000,
    revenueGrowth: 12.5,
  });

  const fetchDashboardStats = async () => {
    const agentId = userData?.id;
    if (!agentId) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/agent/dashboard/stats`,
        { params: { agent_id: agentId } }
      );
      if (res.data.status === 1) {
        const data = res.data.data || {};
        setDashboardData((prev) => ({
          ...prev,
          totalClients: data.total_clients ?? prev.totalClients,
          kycPending: data.kyc_pending ?? prev.kycPending,
        }));
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to load dashboard statistics",
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, [userData?.id]);

  const [recentClients] = useState([
    { id: 1, name: "Rajesh Kumar", kycStatus: "Pending", orders: 3, failedOrders: 1, lastContact: "2 days ago" },
    { id: 2, name: "Priya Sharma", kycStatus: "Verified", orders: 8, failedOrders: 0, lastContact: "1 week ago" },
    { id: 3, name: "Amit Patel", kycStatus: "Pending", orders: 2, failedOrders: 1, lastContact: "3 days ago" },
    { id: 4, name: "Sneha Reddy", kycStatus: "Verified", orders: 5, failedOrders: 0, lastContact: "5 days ago" },
  ]);

  const [pendingTasks] = useState([
    { id: 1, title: "Follow up with Rajesh Kumar for KYC completion", priority: "High", dueDate: "Today", clientName: "Rajesh Kumar" },
    { id: 2, title: "Review failed order for Amit Patel", priority: "Medium", dueDate: "Tomorrow", clientName: "Amit Patel" },
    { id: 3, title: "Contact Priya Sharma for investment update", priority: "Low", dueDate: "2 days", clientName: "Priya Sharma" },
    { id: 4, title: "Complete documentation for new client onboarding", priority: "High", dueDate: "Today", clientName: "New Client" },
  ]);

  const [failedOrders] = useState([
    { id: 1, clientName: "Rajesh Kumar", orderId: "ORD-001", amount: "₹50,000", reason: "Insufficient funds", date: "2 days ago" },
    { id: 2, clientName: "Amit Patel", orderId: "ORD-002", amount: "₹25,000", reason: "KYC incomplete", date: "1 day ago" },
    { id: 3, clientName: "Vikram Singh", orderId: "ORD-003", amount: "₹75,000", reason: "Bank account issue", date: "3 days ago" },
  ]);

  const formatINR = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "#ef4444";
      case "Medium":
        return "#f59e0b";
      case "Low":
        return "#10b981";
      default:
        return "#64748b";
    }
  };

  return (
    <>
      <AdvisorNavbar />
      <div className="page-content" style={{ background: "#f1f5f9" }}>


        {/* Main Content */}

        {/* Welcome Card */}
        <div className="card shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
          {/* <div style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
              padding: "20px",
              borderRadius: "12px",
              borderBottom: "3px solid #39b2b4",
            }}> */}
          <h2 className="mb-1" style={{ color: "#1bafb4 ", }}>
            Welcome, {userData?.first_name && userData?.last_name ? `${userData.first_name} ${userData.last_name}` : "Agent"}
          </h2>
          <p className="mb-0" style={{ color: "#64748b", }}>Agent Dashboard</p>
          {/* </div> */}
        </div>

        {/* BSE Connect */}
        <div className="mb-4">
          <BseLoginCard />
        </div>

        {/* Key Metrics Cards */}
        <div className="row g-3 mb-4">
          {/* Total Clients - click opens Client List (all clients) */}
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div
              className="card shadow-sm h-100"
              style={{ borderRadius: "12px", border: "none", overflow: "hidden", cursor: "pointer", transition: "all 0.2s ease" }}
              onClick={() => navigate("/agent/clients/list")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(27, 175, 180, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <div className="card-body p-4" style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                borderLeft: "4px solid #1bafb4 ",
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ color: "#64748b", fontWeight: "500" }}>Total Clients</p>
                    <h3 className="mb-0" style={{ color: "#1bafb4 " }}>
                      {loading ? "..." : dashboardData.totalClients}
                    </h3>
                  </div>
                  <div style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #ccf1f7 0%, #b3eaf4 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <FiUsers style={{ fontSize: "24px", color: "#1bafb4 " }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Pending - click opens Client List filtered by KYC Pending */}
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div
              className="card shadow-sm h-100"
              style={{ borderRadius: "12px", border: "none", overflow: "hidden", cursor: "pointer", transition: "all 0.2s ease" }}
              onClick={() => navigate("/agent/clients/list?kyc_pending=true")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(245, 158, 11, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <div className="card-body p-4" style={{
                background: "linear-gradient(135deg, #ffffff 0%, #fff7ed 100%)",
                borderLeft: "4px solid #f59e0b",
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ color: "#64748b", fontSize: "14px", fontWeight: "500" }}>KYC Pending</p>
                    <h3 className="mb-0" style={{ color: "#f59e0b", fontSize: "32px", }}>
                      {loading ? "..." : dashboardData.kycPending}
                    </h3>
                  </div>
                  <div style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <FiAlertCircle style={{ fontSize: "24px", color: "#f59e0b" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Failed Orders - click opens Failed Orders screen */}
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div
              className="card shadow-sm h-100"
              style={{ borderRadius: "12px", border: "none", overflow: "hidden", cursor: "pointer", transition: "all 0.2s ease" }}
              onClick={() => navigate("/agent/failed-orders")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(239, 68, 68, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <div className="card-body p-4" style={{
                background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
                borderLeft: "4px solid #ef4444",
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ color: "#64748b", fontSize: "14px", fontWeight: "500" }}>Failed Orders</p>
                    <h3 className="mb-0" style={{ color: "#ef4444", fontSize: "32px", }}>
                      {dashboardData.failedOrders}
                    </h3>
                  </div>
                  <div style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <FiXCircle style={{ fontSize: "24px", color: "#ef4444" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Active Tasks */}
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div className="card shadow-sm h-100" style={{ borderRadius: "12px", border: "none", overflow: "hidden" }}>
              <div className="card-body p-4" style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                borderLeft: "4px solid #10b981",
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ color: "#64748b", fontSize: "14px", fontWeight: "500" }}>Active Tasks</p>
                    <h3 className="mb-0" style={{ color: "#10b981", fontSize: "32px", }}>
                      {dashboardData.activeTasks}
                    </h3>
                  </div>
                  <div style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <FiCheckCircle style={{ fontSize: "24px", color: "#10b981" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Snapshot */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-6">
            <div className="card shadow-sm h-100" style={{ borderRadius: "12px", border: "none" }}>
              <div className="card-body p-4">
                <div style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  borderBottom: "3px solid #39b2b4",
                  marginBottom: "20px",
                }}>
                  <h4 className="mb-0" style={{ color: "#1bafb4 ", fontSize: "20px", }}>
                    {/* Revenue Snapshot */}
                    Mutual Fund Portfolio
                  </h4>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <p className="mb-1" style={{ color: "#64748b", fontSize: "14px" }}>Total Revenue</p>
                    <h3 className="mb-0" style={{ color: "#1bafb4 ", }}>
                      {formatINR(dashboardData.totalRevenue)}
                    </h3>
                  </div>
                  {/* <div style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #ccf1f7 0%, #b3eaf4 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                      <FiDollarSign style={{ color: "#1bafb4 " }} />
                    </div> */}
                </div>
                <div className="d-flex justify-content-between align-items-center pt-3" style={{ borderTop: "1px solid #e2e8f0" }}>
                  <div>
                    <p className="mb-1" style={{ color: "#64748b", fontSize: "14px" }}>This Month</p>
                    <h5 className="mb-0" style={{ color: "#1bafb4 ", fontSize: "20px", fontWeight: "600" }}>
                      {formatINR(dashboardData.monthlyRevenue)}
                    </h5>
                  </div>
                  <div className="d-flex align-items-center" style={{ color: "#10b981" }}>
                    <FiTrendingUp style={{ fontSize: "20px", marginRight: "5px" }} />
                    <span style={{ fontSize: "16px", fontWeight: "600" }}>+{dashboardData.revenueGrowth}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="col-12 col-md-6">
            <div className="card shadow-sm h-100" style={{ borderRadius: "12px", border: "none" }}>
              <div className="card-body p-4">
                <div style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  borderBottom: "3px solid #39b2b4",
                  marginBottom: "20px",
                }}>
                  <h4 className="mb-0" style={{ color: "#1bafb4 ", fontSize: "20px", }}>
                    Quick Actions
                  </h4>
                </div>
                <div className="d-flex flex-column gap-3">
                  <button
                    className="btn w-100 text-start d-flex align-items-center justify-content-between"
                    style={{
                      background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                      border: "2px solid #b3eaf4",
                      borderRadius: "10px",
                      padding: "12px 16px",
                      color: "#1bafb4 ",
                      fontWeight: "500",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)";
                      e.target.style.borderColor = "#39b2b4";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)";
                      e.target.style.borderColor = "#b3eaf4";
                      e.target.style.transform = "translateY(0)";
                    }}
                    onClick={() => navigate("/agent/clients/list")}
                  >
                    <span>Open Client List</span>
                    <FiEye style={{ fontSize: "18px" }} />
                  </button>
                  <button
                    className="btn w-100 text-start d-flex align-items-center justify-content-between"
                    style={{
                      background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                      border: "2px solid #b3eaf4",
                      borderRadius: "10px",
                      padding: "12px 16px",
                      color: "#1bafb4 ",
                      fontWeight: "500",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)";
                      e.target.style.borderColor = "#39b2b4";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)";
                      e.target.style.borderColor = "#b3eaf4";
                      e.target.style.transform = "translateY(0)";
                    }}
                    onClick={() => navigate("/agent/clients/onboard")}
                  >
                    <span>Onboard New Client</span>
                    <FiPlus style={{ fontSize: "18px" }} />
                  </button>
                  <button
                    className="btn w-100 text-start d-flex align-items-center justify-content-between"
                    style={{
                      background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                      border: "2px solid #b3eaf4",
                      borderRadius: "10px",
                      padding: "12px 16px",
                      color: "#1bafb4 ",
                      fontWeight: "500",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)";
                      e.target.style.borderColor = "#39b2b4";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)";
                      e.target.style.borderColor = "#b3eaf4";
                      e.target.style.transform = "translateY(0)";
                    }}
                    onClick={() => navigate("/agent/support/client-tickets")}
                  >
                    <span>Raise Support Ticket</span>
                    <FiFileText style={{ fontSize: "18px" }} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Clients & Tasks Row */}
        <div className="row g-3 mb-4">
          {/* Recent Clients */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100" style={{ borderRadius: "12px", border: "none" }}>
              <div className="card-body p-4">
                <div style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  borderBottom: "3px solid #39b2b4",
                  marginBottom: "20px",
                }}>
                  <h4 className="mb-0" style={{ color: "#1bafb4 ", fontSize: "20px", }}>
                    Recent Clients
                  </h4>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Client Name</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>KYC Status</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Failed Orders</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentClients.map((client) => (
                        <tr key={client.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "12px", color: "#1e293b", fontWeight: "500" }}>{client.name}</td>
                          <td style={{ padding: "12px" }}>
                            <span
                              className="badge"
                              style={{
                                background: client.kycStatus === "Pending" ? "#fef3c7" : "#d1fae5",
                                color: client.kycStatus === "Pending" ? "#f59e0b" : "#10b981",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              {client.kycStatus}
                            </span>
                          </td>
                          <td style={{ padding: "12px", color: "#1e293b" }}>
                            {client.failedOrders > 0 ? (
                              <span style={{ color: "#ef4444", fontWeight: "600" }}>{client.failedOrders}</span>
                            ) : (
                              <span style={{ color: "#10b981" }}>0</span>
                            )}
                          </td>
                          <td style={{ padding: "12px" }}>
                            <button
                              className="btn btn-sm"
                              style={{
                                background: "linear-gradient(135deg, #ccf1f7 0%, #b3eaf4 100%)",
                                color: "#1bafb4 ",
                                border: "none",
                                borderRadius: "6px",
                                padding: "4px 12px",
                                fontSize: "12px",
                                fontWeight: "500",
                              }}
                              onClick={() => navigate(`/agent/clients/profile?id=${client.id}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 text-center">
                  <Link
                    to="/agent/clients/list"
                    style={{
                      color: "#1bafb4 ",
                      textDecoration: "none",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    View All Clients →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Pending Tasks */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm h-100" style={{ borderRadius: "12px", border: "none" }}>
              <div className="card-body p-4">
                <div style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  borderBottom: "3px solid #39b2b4",
                  marginBottom: "20px",
                }}>
                  <h4 className="mb-0" style={{ color: "#1bafb4 ", fontSize: "20px", }}>
                    Pending Tasks
                  </h4>
                </div>
                <div className="d-flex flex-column gap-3">
                  {pendingTasks.map((task) => (
                    <div
                      key={task.id}
                      className="card"
                      style={{
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        background: "#ffffff",
                        padding: "16px",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 110, 130, 0.1)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0" style={{ color: "#1e293b", fontSize: "14px", fontWeight: "600", flex: 1 }}>
                          {task.title}
                        </h6>
                        <span
                          className="badge"
                          style={{
                            background: getPriorityColor(task.priority) + "20",
                            color: getPriorityColor(task.priority),
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                            marginLeft: "8px",
                          }}
                        >
                          {task.priority}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center" style={{ color: "#64748b", fontSize: "12px" }}>
                          <FiClock style={{ fontSize: "14px", marginRight: "4px" }} />
                          <span>Due: {task.dueDate}</span>
                        </div>
                        <button
                          className="btn btn-sm"
                          style={{
                            background: "linear-gradient(135deg, #ccf1f7 0%, #b3eaf4 100%)",
                            color: "#1bafb4 ",
                            border: "none",
                            borderRadius: "6px",
                            padding: "4px 12px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                          onClick={() => navigate(`/agent/clients/profile?task=${task.id}`)}
                        >
                          Create Follow-up
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-center">
                  <button
                    className="btn"
                    style={{
                      background: "linear-gradient(135deg, #1bafb4  0%, #39b2b4 100%)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 20px",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    View All Tasks
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Failed Orders */}
        <div className="row g-3">
          <div className="col-12">
            <div className="card shadow-sm" style={{ borderRadius: "12px", border: "none" }}>
              <div className="card-body p-4">
                <div style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  borderBottom: "3px solid #39b2b4",
                  marginBottom: "20px",
                }}>
                  <h4 className="mb-0" style={{ color: "#1bafb4 ", fontSize: "20px", }}>
                    Failed Orders
                  </h4>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Order ID</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Client Name</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Amount</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Reason</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Date</th>
                        <th style={{ color: "#1bafb4 ", fontSize: "14px", fontWeight: "600", border: "none", padding: "12px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedOrders.map((order) => (
                        <tr key={order.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "12px", color: "#1e293b", fontWeight: "500" }}>{order.orderId}</td>
                          <td style={{ padding: "12px", color: "#1e293b" }}>{order.clientName}</td>
                          <td style={{ padding: "12px", color: "#1e293b", fontWeight: "600" }}>{order.amount}</td>
                          <td style={{ padding: "12px" }}>
                            <span
                              className="badge"
                              style={{
                                background: "#fee2e2",
                                color: "#ef4444",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: "500",
                              }}
                            >
                              {order.reason}
                            </span>
                          </td>
                          <td style={{ padding: "12px", color: "#64748b", fontSize: "14px" }}>{order.date}</td>
                          <td style={{ padding: "12px" }}>
                            <button
                              className="btn btn-sm"
                              style={{
                                background: "linear-gradient(135deg, #ccf1f7 0%, #b3eaf4 100%)",
                                color: "#1bafb4 ",
                                border: "none",
                                borderRadius: "6px",
                                padding: "4px 12px",
                                fontSize: "12px",
                                fontWeight: "500",
                              }}
                              onClick={() => navigate(`/agent/mutual-funds/draft-orders?order=${order.orderId}`)}
                            >
                              Review
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 text-center">
                  <Link
                    to="/agent/mutual-funds/draft-orders"
                    style={{
                      color: "#1bafb4 ",
                      textDecoration: "none",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    View All Failed Orders →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
